import {
  Box,
  Flex,
  PDF_BORDER,
  ReportSection,
  ReportSectionItemContent,
  ReportSectionItemTable,
  type ReportTableCell,
  type ReportTableProps,
} from '@/components';
import { useAccountingTypeOptions } from '@/features/master-table/hooks';
import type { LedgerKsAdditionalInfoSectionProps } from '@/features/mold/models/ledger';
import { formatExpenditureBudgetPurpose } from '@/features/mold/utils/formatter/fund-formatter';
import {
  FundBudgetTypeLabelModifier,
  type FundCompositionBackgroundsModel,
  FundNecessityTypeLabelModifier,
  MeansOfDeliveryLabelModifier,
  ReasonForNotSettingEndPeriodLabelModifier,
  fundBudgetType,
} from '@/features/project/projects/_models/project';
import { useLocaledAmount } from '@/providers';
import {
  FALLBACK_TEXT,
  formatExistenceValue,
  formatValueByUnit,
} from '@/utils';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  LEDGER_BORDER,
  LEDGER_RIGHT_ALIGNED_STYLE,
  fetchProject,
  getEmptySectionTableDataByHeader,
} from '../../../utils';

const TITLE_CELL_FULL_WIDTH = 290;
const TITLE_CELL_NARROW_WIDTH = 200;
const RELATED_REVIEW_SHEET_TITLE_CELL_WIDTH = 75;

export const KsAdditionalInfoSectionBefore2025: FC<
  LedgerKsAdditionalInfoSectionProps
> = ({ project }) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const accountingTypeOptions = useAccountingTypeOptions();
  const fundBudgetTypeOptions = fundBudgetType.options.map((value) => ({
    value,
    label: FundBudgetTypeLabelModifier[value],
  }));

  const nationalTreasuryReturnBackgroundsTableData = useMemo<{
    header: ReportTableProps['header'];
    rowData: ReportTableProps['data'];
  }>(() => {
    const header: ReportTableProps['header'] = [
      {
        type: 'text',
        value: t('fiscal-year-of-national-treasury-return'),
        ratio: 10,
      },
      {
        type: 'text',
        value: t('amount-of-national-treasury-return'),
        ratio: 10,
      },
      {
        type: 'text',
        value: t('reason-of-national-treasury-return'),
        ratio: 48.5,
      },
    ];

    const hasNationalTreasuryReturnBackgrounds =
      project?.fundAdditionalAttributes?.nationalTreasuryReturnBackgrounds &&
      project.fundAdditionalAttributes.nationalTreasuryReturnBackgrounds
        .length > 0;

    const data =
      project?.fundAdditionalAttributes?.nationalTreasuryReturnBackgrounds?.map(
        (d) => {
          return {
            row: [
              { type: 'text', value: d.fiscalYear },
              {
                type: 'text',
                value: formatValueByUnit(d.amount, {
                  operation: 'round',
                  unit,
                }),
                textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
              },
              { type: 'text', value: d.reasons },
            ] as ReportTableCell[],
          };
        },
      ) ?? [];

    return {
      header,
      rowData: hasNationalTreasuryReturnBackgrounds
        ? data
        : getEmptySectionTableDataByHeader(header),
    };
  }, [project?.fundAdditionalAttributes, t, unit]);

  return (
    <ReportSection
      title={t('fund-additional-attributes-before-2025')}
      items={[
        {
          subtitle: t('necessity-of-fund-method'),
          item: (
            <Box>
              <ReportSectionItemContent
                label={t('fund-necessity-type')}
                value={
                  project?.fundAdditionalAttributes?.necessityType
                    ? FundNecessityTypeLabelModifier[
                        project.fundAdditionalAttributes.necessityType
                      ]
                    : null
                }
                labelStyle={{ width: TITLE_CELL_FULL_WIDTH }}
                wrapperStyle={{ borderBottom: LEDGER_BORDER }}
              />
              <ReportSectionItemContent
                isTable
                label={t('fund-necessity-reasons')}
                value={project?.fundAdditionalAttributes?.necessityReasons}
                labelStyle={{ width: TITLE_CELL_FULL_WIDTH }}
              />
            </Box>
          ),
        },
        {
          subtitle: `${t('composition-backgrounds')}\n${t('report-amount-unit-description')}`,
          item: (
            <>
              {project?.fundAdditionalAttributes?.compositionBackgrounds &&
              project.fundAdditionalAttributes.compositionBackgrounds.length >
                0 ? (
                project.fundAdditionalAttributes.compositionBackgrounds.map(
                  (compositionBackground, index, arr) => (
                    <CompositionBackgroundsItem
                      // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                      key={index}
                      isFirstElement={index === 0}
                      isLastElement={index === arr.length - 1}
                      compositionBackground={compositionBackground}
                      accountingTypeOptions={accountingTypeOptions.data ?? []}
                      fundBudgetTypeOptions={fundBudgetTypeOptions}
                    />
                  ),
                )
              ) : (
                <CompositionBackgroundsItem
                  isFirstElement={true}
                  isLastElement={true}
                  compositionBackground={null}
                  accountingTypeOptions={accountingTypeOptions.data ?? []}
                  fundBudgetTypeOptions={fundBudgetTypeOptions}
                />
              )}
            </>
          ),
          wrapperStyle: { breakInside: 'auto' },
        },
        {
          subtitle: `${t('national-treasury-return-backgrounds')}\n${t('report-amount-unit-description')}`,
          item: (
            <ReportSectionItemContent
              isTable
              value={
                <ReportSectionItemTable
                  header={nationalTreasuryReturnBackgroundsTableData.header}
                  data={nationalTreasuryReturnBackgroundsTableData.rowData}
                />
              }
            />
          ),
        },
        {
          subtitle: t('scheduled-end-time'),
          item: (
            <div>
              <Flex>
                <ReportSectionItemContent
                  label={t('fund-scheduled-end-period')}
                  value={
                    project?.fundAdditionalAttributes?.scheduledEnd?.endDate
                  }
                  labelStyle={{ width: TITLE_CELL_NARROW_WIDTH }}
                />
                <ReportSectionItemContent
                  label={t('end-date-of-new-application')}
                  value={
                    project?.fundAdditionalAttributes?.scheduledEnd
                      ?.endDateOfNewApplicationReceiving
                  }
                  wrapperStyle={{ borderLeft: PDF_BORDER.THIN }}
                  labelStyle={{ width: TITLE_CELL_NARROW_WIDTH }}
                />
              </Flex>
              <Flex>
                <ReportSectionItemContent
                  label={t('reason-for-not-setting-scheduled-end-period')}
                  value={
                    project?.fundAdditionalAttributes?.scheduledEnd
                      ?.reasonsForNotSettingScheduledEndPeriod
                      ? ReasonForNotSettingEndPeriodLabelModifier[
                          project.fundAdditionalAttributes.scheduledEnd
                            .reasonsForNotSettingScheduledEndPeriod
                        ]
                      : null
                  }
                  labelStyle={{ width: TITLE_CELL_NARROW_WIDTH }}
                />
                <ReportSectionItemContent
                  label={t(
                    'reason-for-not-setting-end-date-of-new-application',
                  )}
                  value={
                    project?.fundAdditionalAttributes?.scheduledEnd
                      ?.reasonsForNotSettingEndPeriodOfNewApplicationReceiving
                      ? ReasonForNotSettingEndPeriodLabelModifier[
                          project.fundAdditionalAttributes.scheduledEnd
                            .reasonsForNotSettingEndPeriodOfNewApplicationReceiving
                        ]
                      : null
                  }
                  labelStyle={{
                    width: TITLE_CELL_NARROW_WIDTH,
                    borderLeft: PDF_BORDER.THIN,
                  }}
                />
              </Flex>
              <Flex>
                <ReportSectionItemContent
                  label={t('supplementary-reasons')}
                  value={
                    project?.fundAdditionalAttributes?.scheduledEnd
                      ?.supplementaryReasonsForEndPeriod
                  }
                  labelStyle={{ width: TITLE_CELL_NARROW_WIDTH }}
                />
                <ReportSectionItemContent
                  label={t('supplementary-reasons')}
                  value={
                    project?.fundAdditionalAttributes?.scheduledEnd
                      ?.supplementaryReasonsForNewApplicationReceiving
                  }
                  labelStyle={{
                    width: TITLE_CELL_NARROW_WIDTH,
                    borderLeft: PDF_BORDER.THIN,
                  }}
                />
              </Flex>
              <Flex>
                <ReportSectionItemContent
                  isTable
                  label={t('reason-for-changing-scheduled-end-period')}
                  value={
                    project?.fundAdditionalAttributes?.scheduledEnd
                      ?.reasonsForChangingScheduledEndPeriod
                  }
                  labelStyle={{ width: TITLE_CELL_NARROW_WIDTH }}
                />
                <ReportSectionItemContent
                  isTable
                  label={t('reason-for-changing-end-date-of-new-application')}
                  value={
                    project?.fundAdditionalAttributes?.scheduledEnd
                      ?.reasonsForChangingEndPeriodOfNewApplicationReceiving
                  }
                  labelStyle={{
                    width: TITLE_CELL_NARROW_WIDTH,
                    borderLeft: PDF_BORDER.THIN,
                  }}
                />
              </Flex>
            </div>
          ),
        },
        {
          subtitle: t('matter-of-act-for-normalization-of-grant'),
          isLongText: true,
          item: (
            <ReportSectionItemContent
              isLast
              value={
                project?.fundAdditionalAttributes
                  ?.matterOfActForNormalizationOfGrant
              }
            />
          ),
        },
      ]}
    />
  );
};

const CompositionBackgroundsItem = ({
  isFirstElement,
  isLastElement,
  compositionBackground,
  accountingTypeOptions,
  fundBudgetTypeOptions,
}: {
  isFirstElement: boolean;
  isLastElement: boolean;
  compositionBackground: z.infer<typeof FundCompositionBackgroundsModel> | null;
  accountingTypeOptions: { value: string; label: string }[];
  fundBudgetTypeOptions: { value: string; label: string }[];
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const [relatedProjectName, setRelatedProjectName] = useState<string | null>(
    compositionBackground?.relatedReviewSheetName ?? null,
  );

  const fetchRelatedProjectName = async () => {
    if (!compositionBackground?.relatedReviewSheetId) {
      return;
    }
    try {
      const relatedProject = await fetchProject(
        compositionBackground.relatedReviewSheetId,
      );
      setRelatedProjectName(relatedProject?.name);
    } catch {
      return null;
    }
  };

  const basicTableData = useMemo(() => {
    const header: ReportTableProps['header'] = [
      {
        type: 'text',
        value: t('fiscal-year-of-budgetary-provision'),
        ratio: 10,
      },
      {
        type: 'text',
        value: t('fiscal-year-of-creation'),
        ratio: 10,
      },
      {
        type: 'text',
        value: t('means-of-delivery'),
        ratio: 14,
      },
      {
        type: 'text',
        value: t('accounting-type'),
        ratio: 20.5,
      },
      {
        type: 'text',
        value: t('fund-budget-type'),
        ratio: 14,
      },
    ];

    const rowData: ReportTableProps['data'] = [
      {
        row: [
          {
            type: 'text',
            value:
              compositionBackground?.fiscalYearOfBudgetaryProvision ?? null,
          },
          {
            type: 'text',
            value: compositionBackground?.fiscalYearOfCreation ?? null,
          },
          {
            type: 'text',
            value: compositionBackground?.meansOfDelivery
              ? MeansOfDeliveryLabelModifier[
                  compositionBackground.meansOfDelivery
                ]
              : null,
          },
          {
            type: 'text',
            value: compositionBackground?.accountingId
              ? (accountingTypeOptions.find(
                  (type) => type.value === compositionBackground.accountingId,
                )?.label ?? null)
              : null,
          },
          {
            type: 'text',
            value: compositionBackground?.budget
              ? (fundBudgetTypeOptions.find(
                  (type) => type.value === compositionBackground.budget,
                )?.label ?? null)
              : null,
          },
        ] as ReportTableCell[],
      },
    ];

    return {
      header,
      rowData,
    };
  }, [t, compositionBackground, accountingTypeOptions, fundBudgetTypeOptions]);

  const relatedReviewSheetData = useMemo<{
    year: number | null | undefined;
    projectName: string | null;
  }>(() => {
    return {
      year: compositionBackground?.relatedReviewSheetYear,
      projectName: relatedProjectName,
    };
  }, [compositionBackground, relatedProjectName]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: (migrate from ESLint)
  useEffect(() => {
    fetchRelatedProjectName();
  }, []);

  return (
    <Flex
      vertical
      style={{
        breakInside: 'avoid',
        borderTop: isFirstElement ? 0 : LEDGER_BORDER,
        marginTop: isFirstElement ? 0 : 2,
      }}
    >
      <ReportSectionItemContent
        isTable
        value={
          <ReportSectionItemTable
            header={basicTableData.header}
            data={basicTableData.rowData}
          />
        }
      />

      <ReportSectionItemContent
        isTable
        label={t('expenditure-budget-name')}
        value={
          formatExpenditureBudgetPurpose(
            compositionBackground?.expenditureBudgetPurposes,
          )?.join('\n') ?? null
        }
        wrapperStyle={{
          borderTop: LEDGER_BORDER,
          borderBottom: LEDGER_BORDER,
          breakInside: 'auto',
        }}
        labelStyle={{ width: TITLE_CELL_FULL_WIDTH }}
      />
      <ReportSectionItemContent
        label={t('expenditure-budget-name-for-old')}
        value={compositionBackground?.expenditurePurposeName}
        labelStyle={{ width: TITLE_CELL_FULL_WIDTH }}
        wrapperStyle={{ borderBottom: LEDGER_BORDER, breakInside: 'auto' }}
      />

      <Flex style={{ borderBottom: LEDGER_BORDER }}>
        <ReportSectionItemContent
          wrapperStyle={{ borderBottom: 'none' }}
          label={t('amount-of-state-expenditure')}
          value={
            compositionBackground?.amountOfStateExpenditure
              ? formatValueByUnit(
                  compositionBackground.amountOfStateExpenditure,
                  {
                    operation: 'round',
                    unit,
                  },
                )
              : null
          }
          labelStyle={{ width: TITLE_CELL_FULL_WIDTH }}
          valueWrapperStyle={
            compositionBackground?.amountOfStateExpenditure
              ? LEDGER_RIGHT_ALIGNED_STYLE
              : {
                  justifyContent: 'flex-start',
                }
          }
        />
        <ReportSectionItemContent
          wrapperStyle={{ borderBottom: 'none', borderLeft: PDF_BORDER.THIN }}
          label={t('have-applied-law-of-appropriateness-of-subsidies')}
          value={
            compositionBackground?.haveAppliedLawOfAppropriatenessOfSubsidies
              ? formatExistenceValue(
                  compositionBackground.haveAppliedLawOfAppropriatenessOfSubsidies,
                )
              : FALLBACK_TEXT
          }
          labelStyle={{ width: TITLE_CELL_NARROW_WIDTH }}
        />
      </Flex>

      <Flex
        style={{
          width: '100%',
          breakInside: 'avoid-page',
        }}
      >
        <ReportSectionItemContent
          isLast={isLastElement}
          label={t('related-review-sheet')}
          value={
            <ReportSectionItemContent
              label={t('created-year')}
              value={relatedReviewSheetData.year}
              wrapperStyle={{ borderBottom: 'none' }}
              labelStyle={{
                width: RELATED_REVIEW_SHEET_TITLE_CELL_WIDTH,
                minWidth: RELATED_REVIEW_SHEET_TITLE_CELL_WIDTH,
              }}
              valueWrapperStyle={{
                width: 72,
              }}
            />
          }
          labelStyle={{ width: TITLE_CELL_FULL_WIDTH }}
        />
        <ReportSectionItemContent
          isLast={isLastElement}
          label={t('project-name')}
          value={relatedReviewSheetData.projectName}
          wrapperStyle={{
            borderLeft: PDF_BORDER.THIN,
          }}
          labelStyle={{
            width: RELATED_REVIEW_SHEET_TITLE_CELL_WIDTH,
            minWidth: RELATED_REVIEW_SHEET_TITLE_CELL_WIDTH,
          }}
        />
      </Flex>
    </Flex>
  );
};
